// src/stock/DashboardStats.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Button, Spinner
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { COLORS } from "../constants/colors";
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement,
    LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DashboardStats() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchDashboard();
    }, []);

    const fetchDashboard = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stats.php?action=dashboard`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setData(result.data);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       LOADING — identique à l'original
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement du tableau de bord...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="empty-state-charte">
                        <div className="empty-state-icon-charte" style={{ background: 'rgba(239,68,68,0.1)', color: COLORS.danger }}>
                            <i className="ri-error-warning-line"></i>
                        </div>
                        <h6 className="fw-semibold mb-1">Erreur de chargement</h6>
                        <p className="text-muted small mb-3">{error}</p>
                        <Button className="btn-modern-outline" onClick={fetchDashboard}>
                            <i className="ri-refresh-line me-1"></i>Réessayer
                        </Button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       GRAPHIQUES
       ============================================================ */
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const currentMonth = new Date().getMonth();
    const last6Months = months.slice(Math.max(0, currentMonth - 5), currentMonth + 1);
    const caEvolution = [85000, 92000, 78000, 105000, 98000, 112000];
    const topProduits = ['Produit A', 'Produit B', 'Produit C', 'Produit D', 'Produit E'];
    const topVentes = [45, 32, 28, 20, 15];

    const lineChartData = {
        labels: last6Months,
        datasets: [{
            label: 'Chiffre d\'affaires',
            data: caEvolution,
            borderColor: COLORS.primary,
            backgroundColor: `${COLORS.primary}20`,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: COLORS.primary,
            pointBorderColor: 'white',
            pointBorderWidth: 2,
            pointRadius: 4,
        }]
    };

    const lineOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (context) => ` ${formatCurrency(context.parsed.y)}` } }
        },
        scales: {
            y: {
                ticks: { callback: (value) => formatCurrency(value) },
                grid: { color: '#f0f0f0' }
            },
            x: { grid: { display: false } }
        },
        interaction: { intersect: false, mode: 'index' }
    };

    const totalProduits = data?.stock?.total_produits || 1;
    const produitsRupture = data?.stock?.rupture || 0;
    const produitsCritique = data?.stock?.critique || 0;
    const produitsNormaux = totalProduits - produitsRupture - produitsCritique;

    const doughnutData = {
        labels: ['Normal', 'Critique', 'Rupture'],
        datasets: [{
            data: [produitsNormaux, produitsCritique, produitsRupture],
            backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
            borderColor: 'white',
            borderWidth: 3,
        }]
    };

    const doughnutOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    padding: 15, usePointStyle: true, pointStyle: 'circle',
                    font: { size: 12, weight: '500' }
                }
            }
        },
        cutout: '70%',
    };

    const barData = {
        labels: topProduits,
        datasets: [{
            label: 'Quantité vendue',
            data: topVentes,
            backgroundColor: [COLORS.primary, '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'],
            borderColor: 'white',
            borderWidth: 2,
            borderRadius: 6,
        }]
    };

    const barOptions = {
        indexAxis: 'y', responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (context) => `${context.parsed.x} unités` } }
        },
        scales: {
            x: { grid: { color: '#f0f0f0' }, ticks: { stepSize: 5 } },
            y: { grid: { display: false } }
        },
    };

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-dashboard-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Tableau de bord</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-calendar-line"></i> {new Date().toLocaleDateString('fr-FR')}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-line-chart-line"></i> Vue d'ensemble
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchDashboard}>
                                <i className="ri-refresh-line"></i> Actualiser
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/stock/etat/instantane')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* ===== CARTES CA ===== */}
                    <Row className="g-3 mb-4">
                        <Col md="4">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-money-euro-circle-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">CA aujourd'hui</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(data?.ca_jour || 0)}</h3>
                                    <span className="kpi-unit-charte">chiffre du jour</span>
                                </div>
                            </div>
                        </Col>
                        <Col md="4">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-bar-chart-2-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">CA ce mois</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(data?.ca_mois || 0)}</h3>
                                    <span className="kpi-unit-charte">ce mois-ci</span>
                                </div>
                            </div>
                        </Col>
                        <Col md="4">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-line-chart-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">CA cette année</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(data?.ca_an || 0)}</h3>
                                    <span className="kpi-unit-charte">cumul annuel</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== GRAPHIQUES PRINCIPAUX ===== */}
                    <Row className="g-4 mb-4">
                        <Col lg="7">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-bar-chart-line text-white fs-5"></i>
                                        <div>
                                            <span className="fw-semibold text-white d-block">Évolution du chiffre d'affaires</span>
                                            <small className="text-white-50">6 derniers mois</small>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="pt-4" style={{ height: '320px' }}>
                                    <Line data={lineChartData} options={lineOptions} />
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg="5">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-pie-chart-2-line text-white fs-5"></i>
                                        <div>
                                            <span className="fw-semibold text-white d-block">État du stock</span>
                                            <small className="text-white-50">Répartition par niveau</small>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="pt-4" style={{ height: '320px' }}>
                                    <Doughnut data={doughnutData} options={doughnutOptions} />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== GRAPHIQUE TOP PRODUITS + KPI ===== */}
                    <Row className="g-4 mb-4">
                        <Col lg="6">
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-star-line text-white fs-5"></i>
                                        <div>
                                            <span className="fw-semibold text-white d-block">Top 5 produits vendus</span>
                                            <small className="text-white-50">Quantités écoulées</small>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="pt-4" style={{ height: '320px' }}>
                                    <Bar data={barData} options={barOptions} />
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg="6">
                            <Row className="g-3 h-100">
                                <Col sm="6">
                                    <div className="kpi-charte kpi-primary h-100">
                                        <div className="kpi-icon-charte"><i className="ri-group-line"></i></div>
                                        <div className="kpi-content-charte">
                                            <span className="kpi-label-charte">Clients</span>
                                            <h3 className="kpi-value-charte">{formatNumber(data?.clients || 0)}</h3>
                                        </div>
                                    </div>
                                </Col>
                                <Col sm="6">
                                    <div className="kpi-charte kpi-info h-100">
                                        <div className="kpi-icon-charte"><i className="ri-truck-line"></i></div>
                                        <div className="kpi-content-charte">
                                            <span className="kpi-label-charte">Fournisseurs</span>
                                            <h3 className="kpi-value-charte">{formatNumber(data?.fournisseurs || 0)}</h3>
                                        </div>
                                    </div>
                                </Col>
                                <Col sm="6">
                                    <div className="kpi-charte kpi-warning h-100">
                                        <div className="kpi-icon-charte"><i className="ri-shopping-cart-line"></i></div>
                                        <div className="kpi-content-charte">
                                            <span className="kpi-label-charte">Com. en cours</span>
                                            <h3 className="kpi-value-charte">{data?.commandes?.en_cours || 0}</h3>
                                        </div>
                                    </div>
                                </Col>
                                <Col sm="6">
                                    <div className="kpi-charte kpi-success h-100">
                                        <div className="kpi-icon-charte"><i className="ri-check-double-line"></i></div>
                                        <div className="kpi-content-charte">
                                            <span className="kpi-label-charte">Com. reçues</span>
                                            <h3 className="kpi-value-charte">{data?.commandes?.recues || 0}</h3>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    {/* ===== ALERTES STOCK ===== */}
                    {(produitsRupture > 0 || produitsCritique > 0) && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Header className="card-header-gradient" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-notification-3-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Alertes stock</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row className="g-3">
                                    {produitsRupture > 0 && (
                                        <Col md="6">
                                            <div className="alert-card-charte">
                                                <div className="alert-icon-charte" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                                    <i className="ri-close-circle-line"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-bold" style={{ color: COLORS.danger, fontSize: '0.85rem' }}>
                                                        Rupture de stock
                                                    </div>
                                                    <h5 className="mb-1 fw-bold">{produitsRupture} produit(s)</h5>
                                                    <Button variant="link" className="p-0 text-danger btn-link-charte" onClick={() => navigate('/stock/etat/critique')}>
                                                        Voir détails <i className="ri-arrow-right-line"></i>
                                                    </Button>
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                    {produitsCritique > 0 && (
                                        <Col md="6">
                                            <div className="alert-card-charte">
                                                <div className="alert-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                                    <i className="ri-alert-line"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-bold" style={{ color: COLORS.warning, fontSize: '0.85rem' }}>
                                                        Stock critique
                                                    </div>
                                                    <h5 className="mb-1 fw-bold">{produitsCritique} produit(s)</h5>
                                                    <Button variant="link" className="p-0 text-warning btn-link-charte" onClick={() => navigate('/stock/etat/critique')}>
                                                        Voir détails <i className="ri-arrow-right-line"></i>
                                                    </Button>
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== LIENS RAPIDES ===== */}
                    <div className="section-title-charte">
                        <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                            <i className="ri-apps-line"></i>
                        </span>
                        <h6>Analyses détaillées</h6>
                    </div>
                    <Row className="g-3">
                        <Col sm="6" xl="3">
                            <div className="quick-link-card-charte" onClick={() => navigate('/stats/ventes/ca')}>
                                <div className="quick-link-icon-charte">
                                    <i className="ri-money-euro-circle-line"></i>
                                </div>
                                <h6 className="fw-semibold mb-1">Chiffre d'affaires</h6>
                                <small className="text-muted">Évolution et tendances</small>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="quick-link-card-charte" onClick={() => navigate('/stats/ventes/top-produits')}>
                                <div className="quick-link-icon-charte">
                                    <i className="ri-star-line"></i>
                                </div>
                                <h6 className="fw-semibold mb-1">Top produits</h6>
                                <small className="text-muted">Les plus vendus</small>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="quick-link-card-charte" onClick={() => navigate('/stats/achats/depenses')}>
                                <div className="quick-link-icon-charte">
                                    <i className="ri-shopping-cart-2-line"></i>
                                </div>
                                <h6 className="fw-semibold mb-1">Dépenses achats</h6>
                                <small className="text-muted">Analyse des coûts</small>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="quick-link-card-charte" onClick={() => navigate('/stats/stock/rotation')}>
                                <div className="quick-link-icon-charte">
                                    <i className="ri-refresh-line"></i>
                                </div>
                                <h6 className="fw-semibold mb-1">Rotation stock</h6>
                                <small className="text-muted">Performance</small>
                            </div>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== ALERT CARD ===== */
                .alert-card-charte {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 1rem 1.25rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                }
                .alert-card-charte:hover {
                    transform: translateX(5px);
                    border-color: var(--secondary);
                }
                .alert-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .btn-link-charte {
                    font-size: 0.85rem;
                    font-weight: 600;
                    text-decoration: none;
                }

                /* ===== QUICK LINK ===== */
                .quick-link-card-charte {
                    background: white;
                    border-radius: 16px;
                    padding: 1.5rem 1rem;
                    text-align: center;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border: 1px solid #e2e8f0;
                    height: 100%;
                }
                .quick-link-card-charte:hover {
                    transform: translateY(-5px);
                    border-color: var(--primary);
                    box-shadow: 0 20px 25px -5px rgba(33, 172, 205, 0.15);
                }
                .quick-link-icon-charte {
                    width: 56px; height: 56px;
                    background: ${COLORS.primary}15;
                    border-radius: 16px;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 0.75rem;
                    transition: all 0.3s ease;
                }
                .quick-link-icon-charte i {
                    font-size: 1.6rem;
                    color: var(--primary);
                }
                .quick-link-card-charte:hover .quick-link-icon-charte {
                    transform: scale(1.05) rotate(3deg);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}
